import { useState, useEffect, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useWorkspace } from '../context/WorkspaceContext';
import { apiFetch } from '../fetchInterceptor';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Switch } from '../components/ui/switch';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../components/ui/select';
import { ArrowLeft, Clock, Loader2, CheckCircle2 } from 'lucide-react';
import { toast } from 'sonner';
import { formatApiError } from '../lib/formatApiError';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const emptyForm = {
  date: new Date().toISOString().split('T')[0],
  hours: 0, minutes: 0, description: '', task_type: '', contact_id: '',
  billable: true, hourly_rate: '',
};

/**
 * TimeEntryForm — full page replacement for the Manual Time Entry dialog.
 * Routes:
 *   /time-tracking/new       → new entry
 *   /time-tracking/:id/edit  → edit existing entry
 */
export default function TimeEntryForm() {
  const navigate = useNavigate();
  const { entryId } = useParams();
  const { currentWorkspace } = useWorkspace();
  const wsId = currentWorkspace?.workspace_id;

  const [taskTypes, setTaskTypes] = useState([]);
  const [contacts, setContacts] = useState([]);
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(!!entryId);

  const isEdit = !!entryId;

  const fetchRefs = useCallback(async () => {
    if (!wsId) return;
    try {
      const [r1, r2] = await Promise.all([
        apiFetch(`${API_URL}/api/workspaces/${wsId}/time-task-types`),
        apiFetch(`${API_URL}/api/contacts?workspace_id=${wsId}`),
      ]);
      if (r1.ok) setTaskTypes((await r1.json()) || []);
      if (r2.ok) {
        const data = await r2.json();
        setContacts(Array.isArray(data) ? data : (data.contacts || []));
      }
    } catch { /* noop */ }
  }, [wsId]);

  const fetchEntry = useCallback(async () => {
    if (!wsId || !entryId) return;
    setLoading(true);
    try {
      const r = await apiFetch(`${API_URL}/api/workspaces/${wsId}/time-entries/${entryId}`);
      if (r.ok) {
        const e = await r.json();
        // The backend stores hours as a float (e.g. 3.5) plus a separate minutes field.
        // The form uses integer hours + minutes for editing, so split floats out.
        const rawHours = Number(e.hours ?? 0);
        const intHours = Math.floor(rawHours);
        const fractionMinutes = Math.round((rawHours - intHours) * 60);
        const minutes = Number(e.minutes ?? 0) + fractionMinutes;
        setForm({
          date: e.date || '',
          hours: intHours + Math.floor(minutes / 60),
          minutes: minutes % 60,
          description: e.description || '',
          task_type: e.task_type || '',
          contact_id: e.contact_id || '',
          billable: e.billable !== false,
          hourly_rate: e.hourly_rate ?? '',
        });
      } else {
        toast.error(r.status === 404 ? 'Entry not found' : 'Failed to load entry');
        navigate('/time-tracking');
      }
    } finally { setLoading(false); }
  }, [wsId, entryId, navigate]);

  useEffect(() => { fetchRefs(); }, [fetchRefs]);
  useEffect(() => { if (isEdit) fetchEntry(); }, [isEdit, fetchEntry]);

  const save = async () => {
    if (!form.date) return toast.error('Pick a date');
    if (!(form.hours > 0 || form.minutes > 0)) return toast.error('Enter at least some time');
    setSaving(true);
    try {
      const url = isEdit
        ? `${API_URL}/api/workspaces/${wsId}/time-entries/${entryId}`
        : `${API_URL}/api/workspaces/${wsId}/time-entries`;
      // Backend rejects '' for Optional[float]; coerce to null.
      const payload = {
        ...form,
        hourly_rate: (form.hourly_rate === '' || form.hourly_rate === null || form.hourly_rate === undefined)
          ? null
          : Number(form.hourly_rate),
      };
      const r = await apiFetch(url, {
        method: isEdit ? 'PATCH' : 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
      if (r.ok) {
        toast.success(isEdit ? 'Entry updated' : 'Time entry added');
        navigate('/time-tracking');
      } else {
        const err = await r.json().catch(() => ({}));
        toast.error(formatApiError(err, 'Save failed'));
      }
    } finally { setSaving(false); }
  };

  if (loading) return (
    <div className="p-6 flex items-center justify-center text-muted-foreground">
      <Loader2 className="w-5 h-5 animate-spin mr-2" /> Loading entry…
    </div>
  );

  return (
    <div className="container mx-auto p-6 max-w-2xl space-y-5" data-testid="time-entry-form-page">
      <Button variant="ghost" size="sm" onClick={() => navigate('/time-tracking')} data-testid="back-btn">
        <ArrowLeft className="w-4 h-4 mr-1" /> Back to Time Tracking
      </Button>

      <div>
        <div className="flex items-center gap-2">
          <Clock className="w-6 h-6 text-emerald-500" />
          <h1 className="text-2xl font-semibold tracking-tight">{isEdit ? 'Edit time entry' : 'New time entry'}</h1>
        </div>
        <p className="text-sm text-muted-foreground mt-1">
          {isEdit ? 'Update the time you logged for this task.' : 'Log time manually for a task or client.'}
        </p>
      </div>

      <Card>
        <CardContent className="p-5 space-y-4">
          <div className="grid grid-cols-3 gap-3">
            <div className="space-y-2">
              <Label>Date</Label>
              <Input type="date" value={form.date} onChange={e => setForm({...form, date: e.target.value})} data-testid="entry-date" />
            </div>
            <div className="space-y-2">
              <Label>Hours</Label>
              <Input type="number" min="0" value={form.hours} onChange={e => setForm({...form, hours: parseInt(e.target.value) || 0})} data-testid="entry-hours" />
            </div>
            <div className="space-y-2">
              <Label>Minutes</Label>
              <Input type="number" min="0" max="59" value={form.minutes} onChange={e => setForm({...form, minutes: parseInt(e.target.value) || 0})} data-testid="entry-minutes" />
            </div>
          </div>

          <div className="space-y-2">
            <Label>Description</Label>
            <Input value={form.description} onChange={e => setForm({...form, description: e.target.value})} placeholder="What did you work on?" data-testid="entry-description" />
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-2">
              <Label>Task type</Label>
              <Select value={form.task_type} onValueChange={v => setForm({...form, task_type: v})}>
                <SelectTrigger data-testid="entry-task-type"><SelectValue placeholder="Select…" /></SelectTrigger>
                <SelectContent>
                  {taskTypes.map(t => <SelectItem key={t.type_id} value={t.name}>{t.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Client</Label>
              <Select value={form.contact_id} onValueChange={v => setForm({...form, contact_id: v})}>
                <SelectTrigger data-testid="entry-contact"><SelectValue placeholder="None" /></SelectTrigger>
                <SelectContent>
                  {contacts.map(c => <SelectItem key={c.contact_id} value={c.contact_id}>{c.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-3 items-end">
            <div className="flex items-center gap-2">
              <Switch checked={form.billable} onCheckedChange={v => setForm({...form, billable: v})} data-testid="entry-billable" />
              <Label>Billable</Label>
            </div>
            {form.billable && (
              <div className="space-y-2">
                <Label>Hourly rate ($)</Label>
                <Input type="number" min="0" step="0.01" value={form.hourly_rate} onChange={e => setForm({...form, hourly_rate: e.target.value})} placeholder="0.00" data-testid="entry-rate" />
              </div>
            )}
          </div>
        </CardContent>
      </Card>

      <div className="flex items-center justify-between gap-2">
        <Button variant="outline" onClick={() => navigate('/time-tracking')} data-testid="cancel-btn">Cancel</Button>
        <Button onClick={save} disabled={saving} data-testid="save-entry-btn">
          {saving ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <CheckCircle2 className="w-4 h-4 mr-1" />}
          {isEdit ? 'Update entry' : 'Save entry'}
        </Button>
      </div>
    </div>
  );
}
